import React, { useState } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, IconButton, Tooltip, Snackbar, Alert } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { taskService } from '../services/taskService';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

interface DeleteTaskButtonProps {
  taskId: number;
  onDelete?: () => void;
  isIconButton?: boolean;
}

const DeleteTaskButton: React.FC<DeleteTaskButtonProps> = ({ taskId, onDelete, isIconButton = false }) => {
  const [open, setOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleDelete = async () => {
    try {
      setIsDeleting(true);
      await taskService.deleteTask(taskId);
      setIsDeleting(false);
      setOpen(false);
      
      showSnackbar(t('tasks.actions.delete_success'), 'success');
      
      setTimeout(() => {
        navigate('/tasks');
      }, 1000);
      
    } catch (error) {
      console.error('Error deleting task:', error);
      setIsDeleting(false);
      showSnackbar(t('tasks.actions.delete_error'), 'error');
    }
  };

  return (
    <>
      {isIconButton ? (
        <Tooltip title={t('tasks.actions.delete_task')}>
          <IconButton color="error" onClick={handleClickOpen} size="small">
            <DeleteIcon />
          </IconButton>
        </Tooltip>
      ) : (
        <Button 
          variant="contained" 
          color="error" 
          startIcon={<DeleteIcon />} 
          onClick={handleClickOpen}
          fullWidth
        >
          {t('tasks.actions.delete_task')}
        </Button>
      )}

      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {t('tasks.actions.delete_title')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {t('tasks.actions.delete_confirmation')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={isDeleting}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleDelete} color="error" autoFocus disabled={isDeleting}>
            {isDeleting ? t('tasks.actions.deleting') : t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default DeleteTaskButton; 